import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type MobileBatchSurface = 'library' | 'collections-overview' | 'collection-detail' | 'pinned'
export type MobileBatchItemType = 'collections' | 'tours' | 'pois' | 'photos'

const createSet = () => ref<Set<string>>(new Set())

export const useMobileBatchSelectionStore = defineStore('mobileBatchSelection', () => {
  const activeSurface = ref<MobileBatchSurface | null>(null)
  const collections = createSet()
  const tours = createSet()
  const pois = createSet()
  const photos = createSet()

  const isActive = computed(() => activeSurface.value !== null)
  const selectedCount = computed(
    () => collections.value.size + tours.value.size + pois.value.size + photos.value.size,
  )
  const hasSelection = computed(() => selectedCount.value > 0)

  const getSet = (type: MobileBatchItemType) => {
    switch (type) {
      case 'collections':
        return collections
      case 'tours':
        return tours
      case 'pois':
        return pois
      case 'photos':
        return photos
    }
  }

  const clear = () => {
    collections.value = new Set()
    tours.value = new Set()
    pois.value = new Set()
    photos.value = new Set()
  }

  const enter = (surface: MobileBatchSurface) => {
    if (activeSurface.value !== surface) clear()
    activeSurface.value = surface
  }

  const exit = () => {
    activeSurface.value = null
    clear()
  }

  const toggle = (type: MobileBatchItemType, id: string) => {
    const setRef = getSet(type)
    const next = new Set(setRef.value)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setRef.value = next
  }

  const setSelected = (type: MobileBatchItemType, ids: Iterable<string>) => {
    getSet(type).value = new Set(ids)
  }

  const isSelected = (type: MobileBatchItemType, id: string) => getSet(type).value.has(id)

  return {
    activeSurface,
    collections,
    tours,
    pois,
    photos,
    isActive,
    selectedCount,
    hasSelection,
    enter,
    exit,
    clear,
    toggle,
    setSelected,
    isSelected,
  }
})
